const trempMotion = (() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let revealObserver;
  let rafId;
  let scrollTrackingReady = false;
  let stickyAtcBar;
  let stickyAtcObserver;
  let thirdPartyPolishObserver;
  let thirdPartyPolishReady = false;

  const initReveals = (root = document) => {
    const items = root.querySelectorAll('[data-tremp-reveal]:not([data-tremp-ready])');

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => {
        item.dataset.trempReady = 'true';
        item.classList.add('is-visible');
      });
      return;
    }

    revealObserver ||= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    items.forEach((item) => {
      item.dataset.trempReady = 'true';
      revealObserver.observe(item);
    });
  };

  const initPointerGlow = (root = document) => {
    root.querySelectorAll('[data-tremp-glow]:not([data-tremp-glow-ready])').forEach((card) => {
      card.dataset.trempGlowReady = 'true';
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--tremp-glow-x', `${event.clientX - rect.left}px`);
        card.style.setProperty('--tremp-glow-y', `${event.clientY - rect.top}px`);
      });
    });
  };

  const initHero = (root = document) => {
    root.querySelectorAll('[data-tremp-hero]:not([data-tremp-hero-ready])').forEach((hero) => {
      hero.dataset.trempHeroReady = 'true';
      if (reduceMotion.matches) return;

      hero.addEventListener('pointermove', (event) => {
        const rect = hero.getBoundingClientRect();
        hero.style.setProperty('--tremp-pointer-x', ((event.clientX - rect.left) / rect.width - 0.5).toFixed(3));
        hero.style.setProperty('--tremp-pointer-y', ((event.clientY - rect.top) / rect.height - 0.5).toFixed(3));
      });
      hero.addEventListener('pointerleave', () => {
        hero.style.setProperty('--tremp-pointer-x', '0');
        hero.style.setProperty('--tremp-pointer-y', '0');
      });
    });
  };

  const initThirdPartyPolish = () => {
    if (thirdPartyPolishReady) return;

    const applyTidioTheme = () => {
      const shadowRoot = document.querySelector('#tidio-chat')?.shadowRoot;
      if (!shadowRoot || shadowRoot.querySelector('#tremp-tidio-theme')) return false;

      const style = document.createElement('style');
      style.id = 'tremp-tidio-theme';
      style.textContent = `
        #tidio-chat-root {
          --custom-background: #29241f !important;
          --custom-action-color: #b77a2e !important;
          --radius-surface: 20px !important;
          --radius-component: 12px !important;
        }

        @media (max-width: 749px) {
          .widgetLabel {
            display: none !important;
          }

          :host-context(.tremp-sticky-atc-visible) #button {
            transform: translateY(-88px) !important;
          }
        }
      `;
      shadowRoot.append(style);
      thirdPartyPolishReady = true;
      return true;
    };

    if (applyTidioTheme() || thirdPartyPolishObserver) return;

    thirdPartyPolishObserver = new MutationObserver(() => {
      if (!applyTidioTheme()) return;
      thirdPartyPolishObserver.disconnect();
      thirdPartyPolishObserver = undefined;
    });

    thirdPartyPolishObserver.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => {
      thirdPartyPolishObserver?.disconnect();
      thirdPartyPolishObserver = undefined;
    }, 15000);
  };

  const initStickyAtcClearance = (root = document) => {
    const nextBar = root.querySelector?.('.sticky-add-to-cart__bar') || document.querySelector('.sticky-add-to-cart__bar');

    if (nextBar === stickyAtcBar) return;

    stickyAtcObserver?.disconnect();
    stickyAtcBar = nextBar;

    const syncStickyState = () => {
      document.body.classList.toggle('tremp-sticky-atc-visible', stickyAtcBar?.dataset.stuck === 'true');
    };

    if (!stickyAtcBar) {
      syncStickyState();
      return;
    }

    stickyAtcObserver = new MutationObserver(syncStickyState);
    stickyAtcObserver.observe(stickyAtcBar, { attributes: true, attributeFilter: ['data-stuck'] });
    syncStickyState();
  };

  const updateScroll = () => {
    document.querySelectorAll('[data-tremp-hero]').forEach((hero) => {
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      hero.style.setProperty('--tremp-scroll', Math.max(0, -rect.top).toFixed(0));
    });
    rafId = undefined;
  };

  const requestScrollUpdate = () => {
    if (rafId) return;
    rafId = window.requestAnimationFrame(updateScroll);
  };

  const ensureScrollTracking = (root = document) => {
    if (scrollTrackingReady || reduceMotion.matches || !root.querySelector('[data-tremp-hero]')) return;
    scrollTrackingReady = true;
    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener('resize', requestScrollUpdate, { passive: true });
  };

  const init = (root = document) => {
    initReveals(root);
    initPointerGlow(root);
    initHero(root);
    ensureScrollTracking(root);
    initThirdPartyPolish();
    initStickyAtcClearance(root);
    if (scrollTrackingReady) requestScrollUpdate();
  };

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('DOMContentLoaded', () => init(document));

  return { init };
})();

if (document.readyState !== 'loading') trempMotion.init(document);
